import { FileDown, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useFetchAllRiderSales } from "@/hooks/use-rider-sales";
import { downloadPdf } from "@/lib/pdf";

import { RiderSaleReportPdfDocument } from "./rider-sale-report-pdf-document";
import {
  buildRiderSaleReport,
  formatReportPeriod,
} from "./rider-sale-report-utils";

export interface RiderSaleReportPdfButtonProps {
  dateFrom: string;
  dateTo: string;
  workspaceId?: string;
  workspaceName?: string;
  riderId?: string;
}

export function RiderSaleReportPdfButton({
  dateFrom,
  dateTo,
  workspaceId,
  workspaceName = "JAM Coffee & Bakery",
  riderId,
}: RiderSaleReportPdfButtonProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const fetchAllRiderSales = useFetchAllRiderSales();

  async function handleDownload() {
    setIsGenerating(true);
    try {
      const sales = await fetchAllRiderSales({
        date_from: dateFrom,
        date_to: dateTo,
        workspace_id: workspaceId,
        rider_id: riderId,
      });

      if (!sales || sales.length === 0) {
        toast.error(
          `Tidak ada penjualan rider pada periode ${formatReportPeriod(dateFrom, dateTo)}.`,
        );
        return;
      }

      const reportData = buildRiderSaleReport({
        sales,
        dateFrom,
        dateTo,
        workspaceName,
      });

      const filename =
        dateFrom === dateTo
          ? `Penjualan-Rider-${dateFrom}.pdf`
          : `Penjualan-Rider-${dateFrom}-${dateTo}.pdf`;

      await downloadPdf(
        <RiderSaleReportPdfDocument data={reportData} />,
        filename,
      );

      toast.success(
        `Berhasil mengunduh laporan penjualan rider ${formatReportPeriod(dateFrom, dateTo)}.`,
      );
    } catch (err) {
      console.error("Gagal mengunduh laporan penjualan rider:", err);
      toast.error("Gagal membuat PDF laporan penjualan rider.");
    } finally {
      setIsGenerating(false);
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleDownload}
      disabled={isGenerating}
    >
      {isGenerating ? (
        <>
          <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          Membuat PDF...
        </>
      ) : (
        <>
          <FileDown className="mr-1.5 h-4 w-4" />
          Unduh PDF
        </>
      )}
    </Button>
  );
}
